import { useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import { 
  formatPhoneNumber, 
  formatPostalCode, 
  cleanPhoneNumber, 
  cleanPostalCode,
  isValidPhoneNumber,
  isValidPostalCode 
} from '../utils/formatters';
import { getApiUrl, getRecaptchaSiteKey, ApiConfigurationError } from '../utils/apiUtils';

const PlacementForm = () => {
  const { t } = useTranslation();
  const recaptchaSiteKey = useMemo(() => getRecaptchaSiteKey(), []);
  const [formData, setFormData] = useState({
    locationType: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    address: '',
    city: 'Montreal',
    province: 'Quebec',
    postalCode: '',
    dailyTraffic: '',
    employees: '',
    hours: '',
    electrical: '',
    placementLocation: '',
    notes: '',
    source: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    
    // Auto-format phone number
    if (name === 'phone') {
      formattedValue = formatPhoneNumber(value);
    }
    
    // Auto-format postal code
    if (name === 'postalCode') {
      formattedValue = formatPostalCode(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Check if reCAPTCHA is enabled and verify token
    if (recaptchaSiteKey && !recaptchaToken) {
      setSubmitStatus('recaptcha_error');
      setIsSubmitting(false);
      return;
    }

    try {
      // Clean data for backend (remove formatting)
      const cleanedData = {
        ...formData,
        phone: cleanPhoneNumber(formData.phone),
        postalCode: cleanPostalCode(formData.postalCode),
        recaptchaToken: recaptchaToken
      };

      const response = await fetch(getApiUrl('placement.php'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cleanedData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          locationType: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          title: '',
          address: '',
          city: 'Montreal',
          province: 'Quebec',
          postalCode: '',
          dailyTraffic: '',
          employees: '',
          hours: '',
          electrical: '',
          placementLocation: '',
          notes: '',
          source: ''
        });
        // Reset reCAPTCHA
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
          setRecaptchaToken(null);
        }
      } else {
        setSubmitStatus('error');
        // Reset reCAPTCHA on error
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
          setRecaptchaToken(null);
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      if (error instanceof ApiConfigurationError) {
        setSubmitStatus('config_error');
      } else {
        setSubmitStatus('error');
      }
      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Location Type */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          {t('placement_form.form.location_type.label')} *
        </label>
        <select
          name="locationType"
          value={formData.locationType}
          onChange={handleChange}
          required
          className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
        >
          <option value="">Select location type</option>
          {Object.entries(t('placement_form.form.location_type.options', { returnObjects: true })).map(([key, value]) => (
            <option key={key} value={key}>{value}</option>
          ))}
        </select>
      </div>

      {/* Contact Information */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {t('placement_form.form.contact.first_name')} *
          </label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {t('placement_form.form.contact.last_name')} *
          </label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {t('placement_form.form.contact.email')} *
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {t('placement_form.form.contact.phone')} *
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {t('placement_form.form.contact.company')} *
          </label>
          <input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {t('placement_form.form.contact.title')} *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
          />
        </div>
      </div>

      {/* Location Details */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          {t('placement_form.form.location.address')} *
        </label>
        <input
          type="text"
          name="address"
          value={formData.address}
          onChange={handleChange}
          required
          className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {t('placement_form.form.location.city')} *
          </label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {t('placement_form.form.location.province')} *
          </label>
          <input
            type="text"
            name="province"
            value={formData.province}
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {t('placement_form.form.location.postal_code')} *
          </label>
          <input
            type="text"
            name="postalCode"
            value={formData.postalCode}
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
          />
        </div>
      </div>

      {/* Traffic Information */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {t('placement_form.form.traffic.daily_traffic')} *
          </label>
          <select
            name="dailyTraffic"
            value={formData.dailyTraffic}
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
          >
            <option value="">Select traffic range</option>
            {Object.entries(t('placement_form.form.traffic.traffic_options', { returnObjects: true })).map(([key, value]) => (
              <option key={key} value={key}>{value}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {t('placement_form.form.traffic.employees')}
          </label>
          <input
            type="number"
            name="employees"
            value={formData.employees}
            onChange={handleChange}
            className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
          />
        </div>
      </div>

      {/* Operating Hours */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          {t('placement_form.form.hours')} *
        </label>
        <input
          type="text"
          name="hours"
          value={formData.hours}
          onChange={handleChange}
          required
          placeholder="e.g., 9am-5pm Mon-Fri or 24/7"
          className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
        />
      </div>

      {/* Electrical Outlet */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          {t('placement_form.form.electrical.label')} *
        </label>
        <div className="space-y-2">
          {Object.entries(t('placement_form.form.electrical.options', { returnObjects: true })).map(([key, value]) => (
            <label key={key} className="flex items-center">
              <input
                type="radio"
                name="electrical"
                value={key}
                checked={formData.electrical === key}
                onChange={handleChange}
                required
                className="mr-3"
              />
              <span className="text-sm text-slate-700">{value}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Placement Location */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          {t('placement_form.form.placement_location')}
        </label>
        <input
          type="text"
          name="placementLocation"
          value={formData.placementLocation}
          onChange={handleChange}
          placeholder="e.g., Main lobby or 3rd floor break room"
          className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
        />
      </div>

      {/* Additional Notes */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          {t('placement_form.form.notes')}
        </label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={4}
          className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
        />
      </div>

      {/* Source */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          {t('placement_form.form.source.label')}
        </label>
        <select
          name="source"
          value={formData.source}
          onChange={handleChange}
          className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-brand-cyan focus:border-transparent"
        >
          <option value="">Select source</option>
          {Object.entries(t('placement_form.form.source.options', { returnObjects: true })).map(([key, value]) => (
            <option key={key} value={key}>{value}</option>
          ))}
        </select>
      </div>

      {/* reCAPTCHA */}
      {recaptchaSiteKey && (
        <div className="flex justify-center">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={recaptchaSiteKey}
            onChange={handleRecaptchaChange}
            theme="light"
          />
        </div>
      )}

      {/* Submit Button */}
      <div className="pt-6">
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-2xl px-6 py-3 text-sm font-semibold text-white glow disabled:opacity-50"
          style={{ background: 'linear-gradient(90deg, #06b6d4, #3b82f6, #6366f1)' }}
        >
          {isSubmitting ? 'Submitting...' : t('placement_form.form.submit')}
        </button>
      </div>

      {/* Status Messages */}
      {submitStatus === 'success' && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
          <p className="text-sm text-green-800">{t('placement_form.form.success')}</p>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-sm text-red-800">{t('placement_form.form.error')}</p>
        </div>
      )}

      {submitStatus === 'config_error' && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-sm text-red-800">
            Configuration error: API URL is not properly configured. Please contact support.
          </p>
        </div>
      )}

      {submitStatus === 'recaptcha_error' && (
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
          <p className="text-sm text-yellow-800">
            {t('placement_form.form.recaptcha_error_message')}
          </p>
        </div>
      )}
    </form>
  );
};

export default PlacementForm;
